import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "@lingui/macro";
import { PhoneNumberUtil } from "google-libphonenumber";
import { atom, useAtomValue } from "jotai";
import { ReactNode, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  LayoutChangeEvent,
  Pressable as RNPressable,
  ScrollView,
} from "react-native";
import * as yup from "yup";

import { CheckBox } from "@whoosh/design-system/src/components/controls";
import { Button } from "@whoosh/design-system/src/components/controls";
import { Input } from "@whoosh/design-system/src/components/controls/Input";
import { PhoneInput } from "@whoosh/design-system/src/components/controls/PhoneInput";
import { buildFullPhoneNumber } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/buildPhoneNumber";
import { getCountryCodeById } from "@whoosh/design-system/src/components/controls/PhoneInput/utils/countryCodes";
import { FormField } from "@whoosh/design-system/src/components/display";
import {
  EyeIcon,
  InfoIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  Text,
  HStack,
  Box,
} from "@whoosh/design-system/src/components/primitive";

import { openExternalLink } from "../../../../../../../../../utils/openExternalLink";
import { VerificationModalStepStateAtom } from "../../../../atoms/stores";
import { CreateCustomerUserInput } from "../../../../hooks/__generated__/useCreateNewCustomerUserMutation.graphql";
import { useTryToCreateAccount } from "./hooks/useTryToCreateAccount";
import {
  VerifiedContactMethod,
  WHOOSH_PRIVACY_URL,
  WHOOSH_TERMS_URL,
} from "./legalDisclaimerContent";

const assignmentTokenAtom = atom((get) => {
  const state = get(VerificationModalStepStateAtom);
  if (state.step === "AccountCreation") {
    const {
      emailAssignmentToken,
      phoneAssignmentToken,
      verifiedEmail,
      verifiedPhone,
    } = state;
    return {
      emailAssignmentToken,
      phoneAssignmentToken,
      verifiedEmail,
      verifiedPhone,
    };
  }
  return null;
});

export type AccountCreationFormType = {
  firstName: string;
  lastName: string;
  email: string;
  nationalNumber: string;
  countryCode: string;
  password: string;
  agreeToWhooshPolicies: boolean;
  agreeToClubPolicies: boolean;
  allowAnalyticsTracking: boolean;
};

type AccountCreationFormProps = {
  hasAuthenticatedUser?: boolean;
  initialEmail?: string;
  initialPhone?: string;
  verifiedContactMethod: VerifiedContactMethod;
  clubName?: string;
  clubPolicyLink?: string | null;
  defaultCountryCodeId?: string;
  shouldShowAnalyticsTrackingConsent?: boolean;
};

const phoneUtil = PhoneNumberUtil.getInstance();

const buildSchema = (shouldRequireWhooshPolicies: boolean) =>
  yup.object().shape({
    firstName: yup.string().required(),
    lastName: yup.string().required(),
    email: yup.string().email().required(),
    countryCode: yup.string().required(),
    nationalNumber: yup.string().required(),
    password: yup.string().min(8).required(),
    agreeToWhooshPolicies: shouldRequireWhooshPolicies
      ? yup.boolean().required().isTrue()
      : yup.boolean(),
    agreeToClubPolicies: yup.boolean().required().isTrue(),
  });

type RequiredAccountCreationField =
  | "firstName"
  | "lastName"
  | "email"
  | "nationalNumber"
  | "password"
  | "agreeToClubPolicies"
  | "agreeToWhooshPolicies";

const REQUIRED_ACCOUNT_CREATION_FIELDS: RequiredAccountCreationField[] = [
  "firstName",
  "lastName",
  "email",
  "nationalNumber",
  "password",
  "agreeToClubPolicies",
  "agreeToWhooshPolicies",
];

const FIELD_SCROLL_OFFSET = 24;

type ConsentRowProps = {
  isChecked: boolean;
  onToggle: () => void;
  checkboxTestID?: string;
  labelTestID?: string;
  labelContent: ReactNode;
  children?: ReactNode;
};

const ConsentRow = ({
  isChecked,
  onToggle,
  checkboxTestID,
  labelTestID,
  labelContent,
  children,
}: ConsentRowProps) => {
  return (
    <HStack gap={2} alignItems="flex-start">
      <Box paddingTop={1}>
        <CheckBox
          isChecked={isChecked}
          onPress={onToggle}
          testID={checkboxTestID}
        />
      </Box>
      <VStack gap={3} flex={1}>
        <RNPressable onPress={onToggle} testID={labelTestID}>
          {labelContent}
        </RNPressable>
        {children}
      </VStack>
    </HStack>
  );
};

type LegalLinkListItemProps = {
  label: string;
  onPress: () => void;
};

const LegalLinkListItem = ({ label, onPress }: LegalLinkListItemProps) => {
  return (
    <HStack gap={1} alignItems="flex-start">
      <Text variant="mainSmall" color="text-secondary">
        {"\u2022"}
      </Text>
      <Text
        variant="mainSmall"
        color="text-secondary"
        accessibilityRole="link"
        onPress={onPress}
        textDecorationLine="underline"
      >
        {label}
      </Text>
    </HStack>
  );
};

export const AccountCreationForm = ({
  hasAuthenticatedUser = false,
  initialEmail,
  initialPhone,
  verifiedContactMethod,
  clubName,
  defaultCountryCodeId,
  clubPolicyLink,
  shouldShowAnalyticsTrackingConsent,
}: AccountCreationFormProps) => {
  const scrollViewRef = useRef<ScrollView>(null);
  const clubPoliciesOwner = clubName ?? t`Club`;
  const fieldLayoutsRef = useRef<
    Partial<Record<RequiredAccountCreationField, number>>
  >({});
  const shouldShowWhooshPoliciesConsent = verifiedContactMethod === "phone";
  //Get National Number and country code out of phone to set up phone number component.
  const defaultCountryCode = getCountryCodeById(defaultCountryCodeId || "US");

  const parsedPhone = initialPhone
    ? phoneUtil.parseAndKeepRawInput(initialPhone, "US")
    : null;
  const initialCountryCode = parsedPhone
    ? parsedPhone.getCountryCode()?.toString()
    : defaultCountryCode?.code?.toString() || "1";
  const initialNationalNumber = parsedPhone
    ? parsedPhone.getNationalNumber()?.toString()
    : "";

  const {
    control,
    getValues,
    getFieldState,
    setValue,
    trigger,
    formState: { isValid, errors },
  } = useForm<AccountCreationFormType>({
    resolver: yupResolver(buildSchema(shouldShowWhooshPoliciesConsent)),
    mode: "onBlur",
    defaultValues: {
      firstName: "",
      lastName: "",
      email: initialEmail || "",
      countryCode: initialCountryCode || "1",
      nationalNumber: initialNationalNumber || "",
      password: "",
      agreeToClubPolicies: false,
      agreeToWhooshPolicies: !shouldShowWhooshPoliciesConsent,
      allowAnalyticsTracking: false,
    },
  });

  const assignmentTokens = useAtomValue(assignmentTokenAtom);

  const {
    agreeToClubPolicies,
    agreeToWhooshPolicies,
    allowAnalyticsTracking,
    countryCode,
  } = getValues();
  const [shouldShowPassword, setShouldShowPassword] = useState(false);
  const [tryToCreateAccount, isTryingToCreate] =
    useTryToCreateAccount(hasAuthenticatedUser);

  const setFieldLayout =
    (field: RequiredAccountCreationField) => (event: LayoutChangeEvent) => {
      fieldLayoutsRef.current[field] = event.nativeEvent.layout.y;
    };

  const setSharedFieldLayout =
    (fields: RequiredAccountCreationField[]) => (event: LayoutChangeEvent) => {
      fields.forEach((field) => {
        fieldLayoutsRef.current[field] = event.nativeEvent.layout.y;
      });
    };

  const scrollToField = (field: RequiredAccountCreationField) => {
    const yPosition = fieldLayoutsRef.current[field];

    if (yPosition === undefined) {
      return;
    }

    scrollViewRef.current?.scrollTo({
      y: Math.max(yPosition - FIELD_SCROLL_OFFSET, 0),
      animated: true,
    });
  };

  const findFirstInvalidField = () => {
    const values = getValues();

    return REQUIRED_ACCOUNT_CREATION_FIELDS.find((field) => {
      const value = values[field];
      const isEmptyString = typeof value === "string" && value.trim() === "";
      const isUncheckedConsent = typeof value === "boolean" && !value;

      return (
        isEmptyString || isUncheckedConsent || getFieldState(field).invalid
      );
    });
  };

  const handleDisabledSubmitPress = async () => {
    const isFormValid = await trigger();

    if (isFormValid) {
      return;
    }

    const firstInvalidField = findFirstInvalidField();

    if (firstInvalidField) {
      scrollToField(firstInvalidField);
    }
  };

  const submitAccountCreationForm = () => {
    if (isValid) {
      const allValues = getValues();
      const phoneNumber =
        buildFullPhoneNumber(allValues.countryCode, allValues.nationalNumber) ||
        "";
      const emailMatchesVerifiedContact =
        !!assignmentTokens?.verifiedEmail &&
        allValues.email.trim().toLowerCase() ===
          assignmentTokens.verifiedEmail.trim().toLowerCase();
      const phoneMatchesVerifiedContact =
        !!assignmentTokens?.verifiedPhone &&
        phoneNumber === assignmentTokens.verifiedPhone;
      const input: CreateCustomerUserInput = {
        acceptedLiabilityWaiver: allValues.agreeToClubPolicies,
        acceptedTermsUsePrivacyAgreement: allValues.agreeToWhooshPolicies,
        email: allValues.email,
        firstName: allValues.firstName,
        lastName: allValues.lastName,
        password: allValues.password,
        phoneNumber,
        phoneAssignmentToken: phoneMatchesVerifiedContact
          ? assignmentTokens?.phoneAssignmentToken || undefined
          : undefined,
        emailAssignmentToken: emailMatchesVerifiedContact
          ? assignmentTokens?.emailAssignmentToken || undefined
          : undefined,
        analyticsTrackingConsent: shouldShowAnalyticsTrackingConsent
          ? allValues.allowAnalyticsTracking
          : undefined,
      };
      tryToCreateAccount(input);
    }
  };

  return (
    <Box flex={1}>
      <ScrollView ref={scrollViewRef} showsVerticalScrollIndicator={false}>
        <VStack paddingHorizontal={6} paddingBottom={6} flexGrow={1}>
          <VStack gap={6} flexShrink={0} flexGrow={1}>
            <Text variant={"mainTitle2"}>
              {t`Welcome to` + " " + clubName + "!"}
            </Text>
            <Text
              variant="mainBaseMedium"
              color="text-secondary"
            >{t`Create new account to confirm and manage your bookings in our app`}</Text>
            <VStack gap={1}>
              <HStack gap={2}>
                <Box flex={1} onLayout={setFieldLayout("firstName")}>
                  <FormField
                    title={t`First Name`}
                    errorMessage={
                      errors?.firstName?.message ? t`required` : undefined
                    }
                    control={
                      <Controller
                        name="firstName"
                        control={control}
                        render={({ field }) => {
                          return (
                            <Input
                              hasError={!!errors?.firstName?.message}
                              placeholder={t`First Name`}
                              {...field}
                            />
                          );
                        }}
                      />
                    }
                  />
                </Box>

                <Box flex={1} onLayout={setFieldLayout("lastName")}>
                  <FormField
                    title={t`Last Name`}
                    errorMessage={
                      errors?.lastName?.message ? t`required` : undefined
                    }
                    control={
                      <Controller
                        name="lastName"
                        control={control}
                        render={({ field }) => {
                          return (
                            <Input
                              hasError={!!errors?.lastName?.message}
                              placeholder={t`Last Name`}
                              {...field}
                            />
                          );
                        }}
                      />
                    }
                  />
                </Box>
              </HStack>

              <Box onLayout={setFieldLayout("email")}>
                <FormField
                  title={t`Email`}
                  errorMessage={
                    errors?.email?.message ? t`valid email required` : undefined
                  }
                  control={
                    <Controller
                      name="email"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Input
                            hasError={!!errors?.email?.message}
                            placeholder={t`Email`}
                            {...field}
                            isDisabled={!!initialEmail}
                          />
                        );
                      }}
                    />
                  }
                />
              </Box>

              <Box onLayout={setFieldLayout("nationalNumber")}>
                <FormField
                  title={t`Phone`}
                  errorMessage={
                    errors?.nationalNumber?.message
                      ? t`valid phone required`
                      : undefined
                  }
                  control={
                    <Controller
                      name="nationalNumber"
                      control={control}
                      render={({ field }) => {
                        const { value } = field;
                        return (
                          <PhoneInput
                            hasError={!!errors?.nationalNumber?.message}
                            name="phone"
                            onChange={({ countryCode, nationalNumber }) => {
                              setValue("countryCode", countryCode, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                              setValue("nationalNumber", nationalNumber, {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                            }}
                            nationalNumber={value}
                            defaultCountryId={countryCode}
                            placeholder={t`Phone`}
                            isDisabled={!!initialPhone}
                            testID="account-creation-step-phone-input"
                          />
                        );
                      }}
                    />
                  }
                />
              </Box>

              <Box onLayout={setFieldLayout("password")}>
                <FormField
                  title={t`Password`}
                  control={
                    <Controller
                      name="password"
                      control={control}
                      render={({ field }) => {
                        return (
                          <Input
                            hasError={!!errors?.password?.message}
                            type={shouldShowPassword ? "text" : "password"}
                            placeholder={t`Password`}
                            testID="account-creation-step-password-input"
                            rightIcon={
                              <RNPressable
                                accessibilityRole="button"
                                accessibilityLabel={
                                  shouldShowPassword
                                    ? t`Hide password`
                                    : t`Show password`
                                }
                                onPress={() => {
                                  setShouldShowPassword((val) => !val);
                                }}
                              >
                                <EyeIcon />
                              </RNPressable>
                            }
                            {...field}
                          />
                        );
                      }}
                    />
                  }
                />
              </Box>
              <Box marginTop={1}>
                <Box flexDirection="row" gap={1}>
                  <InfoIcon color="text-secondary" />
                  <Text variant="mainTiny" color="text-secondary">
                    {t`At least 8 characters`}
                  </Text>
                </Box>
              </Box>
            </VStack>

            <VStack
              gap={3}
              testID="account-creation-policies-section"
              onLayout={setSharedFieldLayout([
                "agreeToClubPolicies",
                "agreeToWhooshPolicies",
              ])}
            >
              <Text variant="mainHeading">{t`Policies and Conditions`}</Text>
              <Box>
                <ConsentRow
                  isChecked={agreeToClubPolicies}
                  onToggle={() => {
                    setValue("agreeToClubPolicies", !agreeToClubPolicies, {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                  checkboxTestID="account-creation-policies-checkbox"
                  labelTestID="account-creation-policies-label"
                  labelContent={
                    <Text variant="mainSmall" color="text-secondary">
                      {t`By checking this box and submitting this form, you agree to the following policies and terms:`}
                    </Text>
                  }
                >
                  <VStack gap={1} paddingLeft={3}>
                    {clubPolicyLink ? (
                      <LegalLinkListItem
                        label={t`${clubPoliciesOwner} Policies`}
                        onPress={() => {
                          openExternalLink(clubPolicyLink);
                        }}
                      />
                    ) : null}
                    <LegalLinkListItem
                      label={t`Whoosh Terms & Conditions`}
                      onPress={() => {
                        openExternalLink(WHOOSH_TERMS_URL);
                      }}
                    />
                    <LegalLinkListItem
                      label={t`Whoosh Privacy Policy`}
                      onPress={() => {
                        openExternalLink(WHOOSH_PRIVACY_URL);
                      }}
                    />
                  </VStack>
                </ConsentRow>
              </Box>
              {shouldShowWhooshPoliciesConsent ? (
                <Box>
                  <ConsentRow
                    isChecked={agreeToWhooshPolicies}
                    onToggle={() => {
                      setValue(
                        "agreeToWhooshPolicies",
                        !agreeToWhooshPolicies,
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        }
                      );
                    }}
                    checkboxTestID="account-creation-whoosh-policies-checkbox"
                    labelTestID="account-creation-whoosh-policies-label"
                    labelContent={
                      <Text variant="mainSmall" color="text-secondary">
                        {t`By checking this box and submitting this form, you consent to receive transactional text messages for 2FA. Reply STOP to opt out. Reply HELP for help. Standard message and data rates may apply. Message frequency may vary. `}
                      </Text>
                    }
                  ></ConsentRow>
                </Box>
              ) : null}
              {shouldShowAnalyticsTrackingConsent ? (
                <Box>
                  <ConsentRow
                    isChecked={allowAnalyticsTracking}
                    onToggle={() => {
                      setValue(
                        "allowAnalyticsTracking",
                        !allowAnalyticsTracking,
                        {
                          shouldDirty: true,
                          shouldValidate: true,
                        }
                      );
                    }}
                    checkboxTestID="account-creation-analytics-tracking-checkbox"
                    labelTestID="account-creation-analytics-tracking-label"
                    labelContent={
                      <Text variant="mainSmall" color="text-secondary">
                        {t`Optional: allow Whoosh to use product analytics and session replay to improve the app. You can create an account without allowing this tracking.`}
                      </Text>
                    }
                  />
                </Box>
              ) : null}
            </VStack>
          </VStack>
        </VStack>
      </ScrollView>
      <VStack gap={6} marginTop={8} paddingHorizontal={6}>
        <RNPressable
          disabled={isTryingToCreate}
          onPress={isValid ? undefined : handleDisabledSubmitPress}
          testID="account-creation-disabled-submit-target"
        >
          <Button
            accessibilityLabel={t`Create new account button`}
            isLoading={isTryingToCreate}
            isDisabled={!isValid}
            onPress={submitAccountCreationForm}
            pointerEvents={!isValid ? "none" : "auto"}
          >
            {t`Create new Account`}
          </Button>
        </RNPressable>
      </VStack>
    </Box>
  );
};
